import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Pencil, Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ReguaTimeline } from './ReguaTimeline';
import type { CollectionStep } from './ReguaTimeline';
import type { CollectionRule } from '@/pages/ReguaCobranca';
import { ESTADOS_BRASILEIROS } from '@/lib/estados';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface ReguaRowProps {
  rule: CollectionRule;
  customerCount?: number;
  onEdit: () => void;
  onDelete: () => void;
  onAddStep: () => void;
  onEditStep: (step: CollectionStep) => void;
}

function getCriterioText(rule: CollectionRule): string {
  const parts: string[] = [];
  
  // Pontualidade
  if (rule.dias_atraso !== null && rule.dias_atraso !== undefined) {
    if (rule.menos_que) {
      parts.push(`ATRASOU MENOS QUE ${rule.dias_atraso} DIAS`);
    } else {
      parts.push(`ATRASOU ${rule.dias_atraso} DIAS OU MAIS`);
    }
  }
  
  // Idade
  if (rule.idade_min && rule.idade_max) {
    parts.push(`ENTRE ${rule.idade_min} E ${rule.idade_max} ANOS`);
  } else if (rule.idade_min) {
    parts.push(`A PARTIR DE ${rule.idade_min} ANOS`);
  } else if (rule.idade_max) {
    parts.push(`ATÉ ${rule.idade_max} ANOS`);
  }
  
  // Endereço
  if (rule.estado) {
    const estadoNome = ESTADOS_BRASILEIROS.find(e => e.sigla === rule.estado)?.nome || rule.estado;
    if (rule.cidade) {
      parts.push(rule.bairro ? `${rule.bairro}, ${rule.cidade}` : rule.cidade);
    } else {
      parts.push(estadoNome);
    }
  }
  
  return parts.join(' • ') || 'TODOS OS CLIENTES';
}

export function ReguaRow({ rule, customerCount = 0, onEdit, onDelete, onAddStep, onEditStep }: ReguaRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: rule.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto'
  };

  const criterioText = getCriterioText(rule);

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className={`mb-6 ${isDragging ? 'sunk' : ''}`}
    >
      {/* Header Row - Clean layout like reference */}
      <div className="flex items-center gap-4 py-3 px-4">
        {/* Drag Handle */}
        <div 
          className="flex items-center justify-center cursor-grab active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-5 w-5 text-muted-foreground" />
        </div>

        {/* Name */}
        <Link 
          to={`/regua/${rule.id}`}
          className="font-bold text-foreground hover:text-primary transition-colors min-w-[120px]"
        >
          {rule.name}
        </Link>

        {/* Customer Count */}
        <span className="w-24 text-center text-muted-foreground whitespace-nowrap">
          <b className="text-foreground">{customerCount}</b> clientes
        </span>

        {/* Criteria Badge */}
        <div className="flex-1 flex justify-center">
          <span className="inline-flex items-center px-3 py-1.5 rounded border bg-muted/50 text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {criterioText}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-9 w-9 hover:bg-primary/10 hover:text-primary"
                  onClick={onAddStep}
                >
                  <Plus className="h-5 w-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Adicionar comunicação
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-9 w-9 hover:bg-primary/10 hover:text-primary"
            onClick={onEdit}
          >
            <Pencil className="h-5 w-5" />
          </Button>
          
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-9 w-9 text-destructive/70 hover:text-destructive hover:bg-destructive/10"
            onClick={onDelete}
          >
            <Trash2 className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Timeline - Centered layout */}
      <div className="py-6 flex justify-center">
        <ReguaTimeline ruleId={rule.id} onAddStep={onAddStep} onEditStep={onEditStep} />
      </div>
    </div>
  );
}
